import { clsx } from 'clsx';

import { getNodeText } from '../../../internal/utils/getNodeText';
import type { ReeferBaseProps } from '../../../utils/baseProps.types';
import buttonStyles from '../../button/button.module.css';
import type { ButtonProps } from '../../button/button.types';
import { LoadingLabel } from '../../internal/loadingLabel/loadingLabel';
import { Loading } from '../../loading/loading';
import { useReeferTheme } from '../../providers/reeferThemeProvider/reeferThemeProvider';
import { Typography } from '../../typography/typography';
import styles from './cardAction.module.css';

export interface CardActionProps
  extends
    ReeferBaseProps,
    Pick<
      ButtonProps,
      | 'ariaLabel'
      | 'aria-controls'
      | 'disabled'
      | 'endIcon'
      | 'label'
      | 'loading'
      | 'onClick'
      | 'data-testid'
      | 'type'
      | 'variant'
    > {}

/**
 * The `Card.Action` component can be included as a child of a `Card` component to add an
 * action button to the `Card`. Should be used below `Card.Content`.
 */

export function CardAction({
  ariaLabel,
  'aria-controls': ariaControls,
  className,
  disabled,
  endIcon,
  id,
  label,
  loading = false,
  onClick,
  'data-testid': testId,
  type = 'button',
  variant = 'primary',
}: CardActionProps) {
  const theme = useReeferTheme();
  const buttonVariantStyles = {
    ...theme.components.Button.shared,
    ...theme.components.Button.variants[variant],
  };

  return (
    <div className={clsx(styles['cardAction'])}>
      <button
        className={clsx(
          className,
          styles['cardAction_button'],
          buttonStyles['button'],
          buttonStyles['buttonText'],
          buttonStyles['button__full'],
          buttonStyles[`button__${variant}`],
          {
            [buttonStyles['button__disabled']]: disabled,
            [buttonStyles['button__loading']]: loading,
          }
        )}
        aria-label={ariaLabel}
        aria-controls={ariaControls}
        data-testid={testId}
        disabled={disabled}
        id={id}
        name={getNodeText(label)}
        onClick={onClick}
        type={type}
      >
        {loading && <Loading color={buttonVariantStyles.typographyColor} />}
        <LoadingLabel isLoading={loading}>
          <Typography color="inherit" variant="body-bold" as="span">
            {label}
          </Typography>
          {endIcon}
        </LoadingLabel>
      </button>
    </div>
  );
}
